import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import CustomDatePicker from "../customDatePicker/customDatePicker.component";

const WARRANTY_DURATION_YEARS = [1, 2, 3, 5] as const;

// Spelled out per option rather than pluralised through {{count}}: Polish
// needs a different form for 2-3 ("lata") than for 5 ("lat").
const WARRANTY_DURATION_LABELS: Record<(typeof WARRANTY_DURATION_YEARS)[number], string> = {
  1: "1 year",
  2: "2 years",
  3: "3 years",
  5: "5 years",
};

export function calculateWarrantyEndDate(startDate: string, years: number): Dayjs {
  return dayjs(startDate).add(years, "year");
}

interface WarrantyEndDatePickerProps {
  /** `null`/`undefined` means no guarantee recorded. */
  value: Dayjs | null | undefined;
  onChange: (value: Dayjs | null) => void;
  /** The contract start date the duration shortcuts count from, "YYYY-MM-DD".
   *  The shortcuts stay disabled until it is set. */
  startDate?: string;
  helperText?: string;
  size?: "small" | "medium";
  sx?: object;
}

/**
 * Picks the last day of a sold machine's guarantee. The duration buttons are
 * shortcuts only: each one lands on start date + N years, and the date can
 * still be moved by hand afterwards.
 */
export default function WarrantyEndDatePicker({
  value,
  onChange,
  startDate,
  helperText,
  size = "small",
  sx,
}: WarrantyEndDatePickerProps) {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, ...sx }}>
      <CustomDatePicker
        label={t("Warranty end date")}
        value={value ?? null}
        onChange={onChange}
        slotProps={{
          textField: {
            size,
            fullWidth: true,
            helperText,
          },
        }}
      />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {WARRANTY_DURATION_YEARS.map((years) => {
          const isSelected = Boolean(
            startDate &&
              value &&
              value.isSame(calculateWarrantyEndDate(startDate, years), "day")
          );

          return (
            <Button
              key={years}
              size="small"
              variant={isSelected ? "contained" : "outlined"}
              disabled={!startDate}
              onClick={() => startDate && onChange(calculateWarrantyEndDate(startDate, years))}
            >
              {t(WARRANTY_DURATION_LABELS[years])}
            </Button>
          );
        })}
      </Stack>
    </Box>
  );
}
